import './styles/main.css'
import { lazy, Suspense } from 'react';
import ThemeConfig from './styles/ThemeConfig';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthProvider';
import { Box } from '@mui/material';
import Admin from './components/shared/admin/Admin';
import Visitor from './components/shared/visitor/Visitor';
import LoaderCentered from './components/shared/LoaderCentered';

/*
 * Every page is code-split with React.lazy so a visitor only downloads the
 * screens they actually open. Before this the whole app was one ~2 MB bundle
 * (MUI DataGrid + 8 recharts charts + jspdf + moment), which every customer
 * had to download just to view their tire offers.
 * Keep new pages lazy — import them here, never statically.
 */

// Visitor
const Home = lazy(() => import('./pages/visitor/Home'));
const Services = lazy(() => import('./pages/visitor/services/Services'));
const Order = lazy(() => import('./pages/visitor/services/Order'));
const Contact = lazy(() => import('./pages/visitor/contact/Contact'));
const Faq = lazy(() => import('./pages/visitor/faq/Faq'));
const Unregister = lazy(() => import('./pages/visitor/Unregister'));

// Auth
const Login = lazy(() => import('./pages/admin/auth/Login'));
const LoginCallback = lazy(() => import('./pages/admin/auth/LoginCallback'));
const NotAuthorizedAccount = lazy(() => import('./pages/admin/auth/NotAuthorizedAccount'));

// Admin — all staff
const Dashboard = lazy(() => import('./pages/admin/dashboard/Dashboard'));
const DashboardCompare = lazy(() => import('./pages/admin/dashboard/DashboardCompare'));
const Customers = lazy(() => import('./pages/admin/customers/Customers'));
const Offers = lazy(() => import('./pages/admin/offers/Offers'));
const CreateOffer = lazy(() => import('./pages/admin/offers/CreateOffer'));
const Orders = lazy(() => import('./pages/admin/orders/Orders'));
const CreateOrder = lazy(() => import('./pages/admin/orders/CreateOrder'));
const CreateStandardOrder = lazy(() => import('./pages/admin/orders/CreateStandardOrder'));
const Mailings = lazy(() => import('./pages/admin/mailings/Mailings'));
const CreateMailing = lazy(() => import('./pages/admin/mailings/CreateMailing'));
const AdminFaq = lazy(() => import('./pages/admin/faq/AdminFaq'));
const CreateFaq = lazy(() => import('./pages/admin/faq/CreateFaq'));
const Help = lazy(() => import('./pages/admin/help/Help'));

// Admin — settings (Administratör only)
const Settings = lazy(() => import('./pages/admin/settings/Settings'));
const SeasonSettings = lazy(() => import('./pages/admin/settings/SesonSettings'));
const CreateContact = lazy(() => import('./pages/admin/settings/CreateContact'));
const ContactSettings = lazy(() => import('./pages/admin/settings/ContactSettings'));
const WornTireSettings = lazy(() => import('./pages/admin/settings/WornTireSettings'));
const CustomerSettings = lazy(() => import('./pages/admin/settings/CustomerSettings'));
const OfferLinkSettings = lazy(() => import('./pages/admin/settings/OfferLinkSettings'));
const OfferImportSettings = lazy(() => import('./pages/admin/settings/OfferImportSettings'));
const UserSettings = lazy(() => import('./pages/admin/settings/UserSettings'));

// Error pages
const NotFound = lazy(() => import('./components/shared/NotFound'));
const Unauthorized = lazy(() => import('./components/shared/Unauthorized'));

// Roles that matches the roles from the API response
const ROLES = {
  'Admin': 'Administratör',
  'Manager': 'Chef',
  'Employee': 'Anställd'
}

// Shown while a route's chunk is being fetched.
function RouteFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <LoaderCentered width={60} height={60} />
    </Box>
  );
}

export default function App() {
  return (
    // ThemeConfig sets the current theme for the entire app.
    <ThemeConfig>
      {/* BrowserRouter is the router implementation for HTML5 browser */}
      <BrowserRouter>
        {/*
          AuthProvider should be the parent component since
          all components needs the auth context for the protected routes to work.
        */}
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Route is the conditionally shown component based on matching a path to a URL */}
              {/* VISITOR ROUTES*/}
              <Route path="/" element={<Visitor />}>
                <Route path="" element={<Home />} />
                <Route path="services" element={<Services />} />
                <Route path="services/order" element={<Order />} />
                <Route path="contact" element={<Contact />} />
                <Route path="faq" element={<Faq />} />
              </Route>


              {/*
                AUTH ROUTES
                Some routes also require role authorization which is specified with the "allowedRoles" array.
              */}
              <Route path="/admin" element={<Admin allowedRoles={[ROLES.Admin, ROLES.Manager, ROLES.Employee]} />}>
                <Route path="" element={<Navigate replace to="dashboard" />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="dashboard/compare" element={<DashboardCompare />} />
                <Route path="customers" element={<Customers />} />
                <Route path="offers" element={<Offers />} />
                <Route path="offers/create" element={<CreateOffer />} />
                <Route path="orders" element={<Orders />} />
                <Route path="orders/create" element={<CreateOrder />} />
                <Route path="orders/createStandardOrder" element={<CreateStandardOrder />} />

                <Route path="mailings" element={<Mailings />} />
                <Route path="mailings/create" element={<CreateMailing />} />
                <Route path="faq" element={<AdminFaq />} />
                <Route path="faq/create" element={<CreateFaq />} />
                <Route path="help" element={<Help />} />
              </Route>

              <Route path="/admin" element={<Admin allowedRoles={[ROLES.Admin]} />}>
                <Route path="settings" element={<Settings />} />
                <Route path="settings/customer" element={<CustomerSettings />} />
                <Route path="settings/offerLink" element={<OfferLinkSettings />} />
                <Route path="settings/offerImport" element={<OfferImportSettings />} />
                <Route path="settings/users" element={<UserSettings />} />
                <Route path="settings/wornTire" element={<WornTireSettings />} />
                <Route path="settings/seasons" element={<SeasonSettings />} />
                <Route path="settings/contact" element={<ContactSettings />} />
                <Route path="settings/contact/create" element={<CreateContact />} />
              </Route>


              {/* OTHER ROUTES */}
              <Route path="/login" element={<Login />} />
              {/* Landing page for the Azure AD login redirect from the API */}
              <Route path="/login/callback" element={<LoginCallback />} />
              {/* Shown when an Azure AD account is not a member of any Hjulle group */}
              <Route path="/unauthorized-account" element={<NotAuthorizedAccount />} />
              <Route path="/unregister" element={<Unregister />} />


              {/* ERROR ROUTES */}
              {/* 401 route, when an authenticated user is unauthorized */}
              {/* Insert 403 page here? */}
              <Route path="/unauthorized" element={<Unauthorized />} />
              {/* 404 route, when the URL path doesn't match a route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </ThemeConfig>
  );
}
